import {
  AdjustmentsHorizontalIcon,
  ArrowRightIcon,
} from '@heroicons/react/20/solid'
import store from 'stores/store'
import { SHOW_SWAP_CHART_KEY, SHOW_SWAP_ORDERBOOK_KEY } from 'utils/constants'
import { useState } from 'react'
import TradeTabs from '@components/trade/TradeTabs'
import Loading from '@components/loading/Loading'
import dynamic from 'next/dynamic'
import Orderbook from '@components/orderbook/Orderbook'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import Switch from '@components/forms/Switch'
import Button, { LinkButton } from '@components/shared/Button'
import { useTranslation } from 'next-i18next'
import Modal from '@components/shared/Modal'
import { ModalProps } from 'types/modal'
import SwapAnnouncement from '@components/announcements/SwapAnnouncement'
import { TokenAddress } from '@components/shared/TokenAddress'

const TradingViewChart = dynamic(
  () => import('@components/trade/TradingViewChart'),
  {
    ssr: false,
  },
)

const SwapPage = () => {
  const { t } = useTranslation('swap')
  const inputToken = store((s) => s.swap.inputToken)
  const outputToken = store((s) => s.swap.outputToken)
  const [showChart] = useLocalStorage(SHOW_SWAP_CHART_KEY)
  const [showOrderbook] = useLocalStorage(SHOW_SWAP_ORDERBOOK_KEY)
  const [showDisplayOptionsModal, setShowDisplayOptionsModal] = useState(false)

  if (!inputToken.mint || !outputToken.mint) {
    return (
      <div className="flex h-[calc(100vh-368px)] flex-col items-center justify-center">
        <Loading />
      </div>
    )
  }

  // const { data: outputTokenSummary } = useQuery({
  //   queryKey: ['output-token-summary', outputToken.mint],
  //   queryFn: () => fetchMarketDataForMint(outputToken.mint),
  //   cacheTime: milliseconds({ minutes: 30 }),
  //   staleTime: milliseconds({ minutes: 30 }),
  //   retry: 3,
  //   refetchOnWindowFocus: false,
  //   enabled: !!outputToken.mint && outputToken.mint !== USDC_MINT,
  // })

  // const { data: inputTokenSummary } = useQuery({
  //   queryKey: ['input-token-summary', inputToken.mint],
  //   queryFn: () => fetchMarketDataForMint(inputToken.mint),
  //   cacheTime: milliseconds({ minutes: 30 }),
  //   staleTime: milliseconds({ minutes: 30 }),
  //   retry: 3,
  //   refetchOnWindowFocus: false,
  //   enabled: !!inputToken.mint && inputToken.mint !== USDC_MINT,
  // })

  return (
    <>
      <div className="flex justify-center">
        <SwapAnnouncement />
      </div>
      <div
        className={`mx-auto flex max-w-[1680px] flex-col gap-4 ${
          showOrderbook && !showChart ? 'md:flex-row' : 'lg:flex-row'
        } lg:items-start lg:justify-center`}
      >
        {showChart || showOrderbook ? (
          <div
            className={`order-2 flex flex-col gap-4 md:flex-row lg:order-1 ${
              showChart ? 'lg:w-full' : 'lg:w-auto'
            }`}
          >
            {showChart ? (
              <div className="shared-container order-2 h-[360px] w-full p-2 sm:p-4 lg:order-1 lg:mt-6 lg:h-[580px] lg:p-6">
                <TradingViewChart />
              </div>
            ) : null}
            {showOrderbook ? (
              <div className="shared-container order-3 flex h-[360px] w-full items-stretch justify-stretch md:w-[260px] lg:order-2 lg:mt-6 lg:h-[580px]">
                <div className="m-2 mt-0 flex-1 overflow-hidden sm:m-4 sm:mt-0">
                  <Orderbook noDataClassName="mt-2 sm:mt-4" />
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        <div
          className={`order-1 lg:order-2 lg:max-w-lg ${
            !showChart ? 'w-full' : 'w-auto'
          }`}
        >
          <div className="flex items-center justify-end pb-2">
            <LinkButton
              className="flex items-center gap-1.5"
              onClick={() => setShowDisplayOptionsModal(true)}
            >
              <AdjustmentsHorizontalIcon className="size-3" />
              <span className="text-xs">{t('display-options')}</span>
            </LinkButton>
          </div>
          <div className="shared-container relative min-w-[330px]">
            <TradeTabs />
          </div>
          <div className="flex items-center justify-center gap-2 pt-4">
            <TokenAddress
              className="flex items-center"
              token={inputToken}
              addressAsLink
              showTokenImage
            />
            <ArrowRightIcon className="size-4 text-th-fgd-3" />
            <TokenAddress
              className="flex items-center"
              token={outputToken}
              addressAsLink
              showTokenImage
            />
          </div>
          {/* <div className="space-y-2 pt-6">
            <TokenSummary
              mint={inputToken.mint}
              imageUrl={inputToken.image_url}
              symbol={inputToken.symbol}
              tokenPriceChange24h={inputTokenSummary?.d1?.price?.change}
            />
            <TokenSummary
              mint={outputToken.mint}
              imageUrl={outputToken.image_url}
              symbol={outputToken.symbol}
              tokenPriceChange24h={outputTokenSummary?.d1?.price?.change}
            />
          </div> */}
        </div>
      </div>
      {showDisplayOptionsModal ? (
        <DisplayOptionsModal
          isOpen={showDisplayOptionsModal}
          onClose={() => setShowDisplayOptionsModal(false)}
        />
      ) : null}
    </>
  )
}

export default SwapPage

const DisplayOptionsModal = ({ isOpen, onClose }: ModalProps) => {
  const { t } = useTranslation('swap')
  const [showChart, setShowChart] = useLocalStorage(SHOW_SWAP_CHART_KEY)
  const [showOrderbook, setShowOrderbook] = useLocalStorage(
    SHOW_SWAP_ORDERBOOK_KEY,
  )
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <h2 className="mb-4 text-center">{t('display-options')}</h2>
      <div className="mb-4 flex items-center justify-between">
        <span className="text-th-fgd-2">{t('chart')}</span>
        <Switch checked={showChart} onChange={() => setShowChart(!showChart)} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-th-fgd-2">{t('orderbook')}</span>
        <Switch
          checked={showOrderbook}
          onChange={() => setShowOrderbook(!showOrderbook)}
          className=""
        />
      </div>
      <Button className="mt-6 w-full" onClick={onClose}>
        {t('done')}
      </Button>
    </Modal>
  )
}

// const fetchPriceHistory = async (mint: string) => {
//   const url = new URL(`${TRAFFIC_API_URL}/candles`)
//   const now = new Date()
//   const params = new URLSearchParams({
//     base_mint: mint,
//     quote_mint: USDC_MINT,
//     period: 'H1',
//     from: String(getUnixTime(subDays(now, 1))),
//     to: String(getUnixTime(now)),
//     limit: String(24),
//   })
//   url.search = params.toString()

//   try {
//     const response = await fetch(url)
//     const data = await response.json()
//     if (data && data?.candles?.length) {
//       return data.candles
//     }
//     return []
//   } catch (e) {
//     console.error('failed to fetch price history', e)
//     return []
//   }
// }

// const metadataCache = new Map<string, TokenMetadataItem[]>()

// type PriceHistory = {
//   close: number
//   timestamp: number
// }

// const TokenSummary = ({
//   mint,
//   imageUrl,
//   symbol,
//   tokenPriceChange24h,
// }: {
//   mint: string
//   imageUrl: string | undefined
//   symbol: string | undefined
//   tokenPriceChange24h: number | undefined | null
// }) => {
//   const connection = store((s) => s.connection)
//   const { data: tokenInfo } = useQuery({
//     queryKey: ['backup-swap-token-info', mint],
//     queryFn: () => fetchMetadataForMints([mint], metadataCache, connection),
//     cacheTime: milliseconds({ minutes: 30 }),
//     staleTime: milliseconds({ minutes: 30 }),
//     retry: 3,
//     enabled: !!mint && !symbol,
//     refetchOnWindowFocus: false,
//   })

//   const { data: priceHistory } = useQuery({
//     queryKey: ['token-price-history', mint],
//     queryFn: () => fetchPriceHistory(mint),
//     cacheTime: milliseconds({ minutes: 30 }),
//     staleTime: milliseconds({ minutes: 30 }),
//     retry: 3,
//     enabled: !!mint,
//     refetchOnWindowFocus: false,
//   })

//   const image = imageUrl || tokenInfo?.[0]?.image_url
//   const chartData =
//     priceHistory && priceHistory?.length
//       ? priceHistory
//           .map((hist: PriceHistory) => ({
//             price: hist.close,
//             time: hist.timestamp,
//           }))
//           .reverse()
//       : []

//   const price = chartData.length ? chartData[chartData.length - 1].price : 0

//   return (
//     <Link
//       className="shared-container block w-full p-6 md:hover:bg-th-bkg-2"
//       href={`/token/${mint}`}
//     >
//       <div className="flex items-center justify-between">
//         <div className="flex items-center space-x-2">
//           {image ? (
//             <ImageWithFallback
//               src={image}
//               height={32}
//               width={32}
//               alt={`${symbol || tokenInfo?.[0]?.symbol || ''} token logo`}
//             />
//           ) : (
//             <QuestionMarkCircleIcon className="size-8 text-th-fgd-4" />
//           )}
//           <div>
//             <p className="mb-1 font-semibold text-th-fgd-1">
//               {symbol || tokenInfo?.[0]?.symbol
//                 ? symbol || tokenInfo?.[0]?.symbol
//                 : '–'}
//               <span
//                 className={`ml-2 font-mono ${
//                   tokenPriceChange24h && price
//                     ? tokenPriceChange24h < 0
//                       ? 'text-th-down'
//                       : 'text-th-up'
//                     : 'text-th-fgd-4'
//                 }`}
//               >
//                 {price ? `$${formatNumericValue(price)}` : '–'}
//               </span>
//             </p>
//             {mint ? (
//               <AddressDisplay className="flex w-max items-center">
//                 <p className="text-xs">{abbreviateAddress(mint)}</p>
//               </AddressDisplay>
//             ) : (
//               <p>–</p>
//             )}
//           </div>
//         </div>
//         {chartData.length ? (
//           <div className="h-10 w-28">
//             <SimpleAreaChart
//               color={
//                 chartData[0].price <= chartData[chartData.length - 1].price
//                   ? 'var(--up)'
//                   : 'var(--down)'
//               }
//               data={chartData}
//               name={mint}
//               xKey="time"
//               yKey="price"
//             />
//           </div>
//         ) : null}
//       </div>
//     </Link>
//   )
// }
